import React from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Users, Calendar, Settings, Bot, CheckCircle, TrendingUp } from 'lucide-react';

const ProductsPage = () => {
  const products = [
    {
      icon: Users,
      title: 'Unisales-CRM',
      subtitle: 'Gestão de Relacionamento com Clientes',
      description: 'Solução completa para gestão de potenciais clientes, oportunidades e relacionamento com clientes. Aumente as suas vendas e melhore a satisfação dos clientes.',
      features: ['Gestão de leads e oportunidades', 'Automação de vendas', 'Análise de desempenho', 'Integração com redes sociais'],
      benefits: ['30% mais conversões', 'Redução de 40% no tempo de resposta', 'Aumento de 85% na retenção'],
      path: '/crm',
      color: 'blue',
      price: 'A partir de €49/mês+IVA'
    },
    {
      icon: Calendar,
      title: 'Unisales-Eventos',
      subtitle: 'Gestão de Eventos Completa',
      description: 'Plataforma completa para organização de eventos. Da inscrição ao feedback, automatize todos os processos e tenha eventos de sucesso.',
      features: ['Inscrições online', 'Controlo de pagamentos', 'Certificados digitais', 'Aplicação móvel'],
      benefits: ['10+ eventos organizados', '95% satisfação dos clientes', 'Redução de 60% em tarefas manuais'],
      path: '/eventos',
      color: 'green',
      price: 'A partir de €29/mês+IVA'
    },
    {
      icon: Settings,
      title: 'Unisales-ERP',
      subtitle: 'Sistema Integrado de Gestão',
      description: 'Integre todos os processos do seu negócio numa única plataforma. Controlo total sobre operações, finanças e recursos humanos.',
      features: ['Gestão de stock', 'Controlo financeiro', 'Recursos humanos', 'Relatórios avançados'],
      benefits: ['Redução de 25% nos custos', 'Integração 100%', 'Suporte 24/7'],
      path: '/erp',
      color: 'purple',
      price: 'A partir de €99/mês+IVA'
    },
    {
      icon: Bot,
      title: 'Unisales-IA',
      subtitle: 'Inteligência Artificial Empresarial',
      description: 'Potencialize o seu negócio com IA avançada. Tome decisões inteligentes, automatize processos e antecipe tendências.',
      features: ['Chatbots inteligentes', 'Análise preditiva', 'Automação de marketing', 'Insights em tempo real'],
      benefits: ['90% precisão nas previsões', 'Redução de 60% no tempo de resposta', 'Otimização contínua'],
      path: '/ia',
      color: 'orange',
      price: 'A partir de €79/mês+IVA'
    }
  ];

  const scrollToComparison = () => {
    const comparisonSection = document.getElementById('comparison');
    if (comparisonSection) {
      comparisonSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <>
      <Header />
      <main className="min-h-screen bg-gray-50">
        {/* Hero Section */}
        <section className="bg-gradient-to-r from-blue-600 to-purple-600 text-white py-20 pt-32">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <h1 className="text-5xl font-bold mb-6">Os Nossos Produtos</h1>
              <p className="text-xl mb-8 text-blue-100">
                Descubra as soluções Unisales que podem transformar o seu negócio.
                Cada produto foi desenvolvido para otimizar processos e impulsionar resultados.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/contacto" className="bg-white text-blue-700 px-8 py-3 rounded-full font-semibold hover:bg-blue-50 transition duration-300">
                  Agendar Demonstração
                </Link>
                <button
                  onClick={scrollToComparison}
                  className="border-2 border-white text-white px-8 py-3 rounded-full font-semibold hover:bg-white hover:text-blue-700 transition duration-300"
                >
                  Comparar Produtos
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* Products Grid */}
        <section className="py-20">
          <div className="container mx-auto px-4">
            <div className="grid lg:grid-cols-2 gap-8">
              {products.map((product, index) => {
                const IconComponent = product.icon;
                return (
                  <div key={index} className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition duration-300 border border-gray-100">
                    <div className="flex items-start justify-between mb-6">
                      <div className={`w-16 h-16 bg-${product.color}-100 rounded-full flex items-center justify-center`}>
                        <IconComponent className={`w-8 h-8 text-${product.color}-600`} />
                      </div>
                      <div className="text-right">
                        <div className="text-sm text-gray-500">A partir de</div>
                        <div className="text-lg font-bold text-gray-800">{product.price}</div>
                      </div>
                    </div>

                    <h3 className="text-2xl font-bold mb-2">{product.title}</h3>
                    <p className="text-sm text-gray-600 mb-4">{product.subtitle}</p>
                    <p className="text-gray-600 mb-4 leading-relaxed">{product.description}</p>
                    <Link to={product.path} className="text-blue-600 font-semibold hover:text-blue-800 transition duration-300 inline-block mb-6">Saiba mais sobre esta solução.</Link>

                    <div className="mb-6">
                      <h4 className="font-semibold mb-3 text-gray-800">Funcionalidades Principais:</h4>
                      <ul className="space-y-2">
                        {product.features.map((feature, idx) => (
                          <li key={idx} className="flex items-center text-sm text-gray-600">
                            <CheckCircle className="w-4 h-4 text-green-500 mr-2 flex-shrink-0" />
                            {feature}
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div className="mb-6">
                      <h4 className="font-semibold mb-3 text-gray-800">Benefícios:</h4>
                      <div className="grid grid-cols-1 gap-2">
                        {product.benefits.map((benefit, idx) => (
                          <div key={idx} className="flex items-center text-sm text-gray-600">
                            <TrendingUp className="w-4 h-4 text-blue-500 mr-2 flex-shrink-0" />
                            {benefit}
                          </div>
                        ))}
                      </div>
                    </div>

                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Comparison Table */}
        <section id="comparison" className="py-20 bg-white">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-3xl font-bold mb-4">Comparação de Produtos</h2>
              <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                Escolha a solução ideal para as necessidades do seu negócio
              </p>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full bg-white rounded-lg shadow-lg">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="px-6 py-4 text-left font-semibold">Funcionalidade</th>
                    <th className="px-6 py-4 text-center font-semibold">CRM</th>
                    <th className="px-6 py-4 text-center font-semibold">Eventos</th>
                    <th className="px-6 py-4 text-center font-semibold">ERP</th>
                    <th className="px-6 py-4 text-center font-semibold">IA</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-t">
                    <td className="px-6 py-4 font-medium">Gestão de Clientes</td>
                    <td className="px-6 py-4 text-center"><CheckCircle className="w-5 h-5 text-green-500 mx-auto" /></td>
                    <td className="px-6 py-4 text-center"><CheckCircle className="w-5 h-5 text-green-500 mx-auto" /></td>
                    <td className="px-6 py-4 text-center">-</td>
                    <td className="px-6 py-4 text-center"><CheckCircle className="w-5 h-5 text-green-500 mx-auto" /></td>
                  </tr>
                  <tr className="border-t bg-gray-50">
                    <td className="px-6 py-4 font-medium">Automação</td>
                    <td className="px-6 py-4 text-center"><CheckCircle className="w-5 h-5 text-green-500 mx-auto" /></td>
                    <td className="px-6 py-4 text-center"><CheckCircle className="w-5 h-5 text-green-500 mx-auto" /></td>
                    <td className="px-6 py-4 text-center"><CheckCircle className="w-5 h-5 text-green-500 mx-auto" /></td>
                    <td className="px-6 py-4 text-center"><CheckCircle className="w-5 h-5 text-green-500 mx-auto" /></td>
                  </tr>
                  <tr className="border-t">
                    <td className="px-6 py-4 font-medium">Relatórios</td>
                    <td className="px-6 py-4 text-center"><CheckCircle className="w-5 h-5 text-green-500 mx-auto" /></td>
                    <td className="px-6 py-4 text-center"><CheckCircle className="w-5 h-5 text-green-500 mx-auto" /></td>
                    <td className="px-6 py-4 text-center"><CheckCircle className="w-5 h-5 text-green-500 mx-auto" /></td>
                    <td className="px-6 py-4 text-center"><CheckCircle className="w-5 h-5 text-green-500 mx-auto" /></td>
                  </tr>
                  <tr className="border-t bg-gray-50">
                    <td className="px-6 py-4 font-medium">Integração API</td>
                    <td className="px-6 py-4 text-center"><CheckCircle className="w-5 h-5 text-green-500 mx-auto" /></td>
                    <td className="px-6 py-4 text-center"><CheckCircle className="w-5 h-5 text-green-500 mx-auto" /></td>
                    <td className="px-6 py-4 text-center"><CheckCircle className="w-5 h-5 text-green-500 mx-auto" /></td>
                    <td className="px-6 py-4 text-center"><CheckCircle className="w-5 h-5 text-green-500 mx-auto" /></td>
                  </tr>
                  <tr className="border-t">
                    <td className="px-6 py-4 font-medium">Suporte 24/7</td>
                    <td className="px-6 py-4 text-center"><CheckCircle className="w-5 h-5 text-green-500 mx-auto" /></td>
                    <td className="px-6 py-4 text-center"><CheckCircle className="w-5 h-5 text-green-500 mx-auto" /></td>
                    <td className="px-6 py-4 text-center"><CheckCircle className="w-5 h-5 text-green-500 mx-auto" /></td>
                    <td className="px-6 py-4 text-center"><CheckCircle className="w-5 h-5 text-green-500 mx-auto" /></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-gradient-to-r from-blue-600 to-purple-600 text-white">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl font-bold mb-4">Pronto para começar?</h2>
            <p className="text-xl mb-8 text-blue-100">
              Agende uma demonstração gratuita e veja como podemos ajudar o seu negócio a crescer.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contacto" className="bg-white text-blue-700 px-8 py-3 rounded-full font-semibold hover:bg-blue-50 transition duration-300">
                Agendar Demonstração Gratuita
              </Link>
              <Link to="/sobre" className="border-2 border-white text-white px-8 py-3 rounded-full font-semibold hover:bg-white hover:text-blue-700 transition duration-300">
                Saber Mais Sobre Nós
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
};

export default ProductsPage;
